import { OnDataOptions } from '@apollo/client';
import { ClipObject } from '../../../../context/clips';
import {
  useClipEntityChangedSubscription,
  ClipEntityChangedSubscription,
} from '../../operation.generated';
import {
  EntityChangeType,
  ClipEntityChangedEvent,
} from '../../../../../server/graphql/__generated.types';
import useRealtimeClipLogger from './useRealtimeClipLogger';
import { ClipEventRealtime } from '../../../../services/EventTrackingService';
import useClipCacheUtilities from './useClipCacheUtilities';
import { useAudioContext } from '../../../../context/audio/AudioContext';
import { usePersistentClipStateAndActions } from './common';
import useIsOutOfOrderEvent from '../../../../util/subscriptionUtils';

type RealtimeEventProps = {
  changeType: EntityChangeType;
  projectId: string;
  incomingClip: ClipObject;
  clipInCache?: ClipObject;
};

type ManageIncomingClipEventProps = {
  incomingClip: ClipObject;
  clipInCache?: ClipObject;
  event: ClipEntityChangedEvent;
  projectId: string;
  changeType: EntityChangeType;
};

/**
 * Calculate mixpanel realtime event types
 */
export function calculateRealtimeEvent({
  changeType,
  projectId,
  incomingClip,
  clipInCache,
}: RealtimeEventProps) {
  if (changeType === EntityChangeType.Created) {
    return ClipEventRealtime.Render;
  }
  if (changeType === EntityChangeType.Deleted) {
    return ClipEventRealtime.Deleted;
  }
  if (incomingClip.projectId !== projectId) {
    return ClipEventRealtime.MovedProject;
  }
  // note this is not 100% reliable on the active client
  if (clipInCache && clipInCache.isDeleted && !incomingClip.isDeleted) {
    return ClipEventRealtime.Restored;
  }
  if (clipInCache && clipInCache.sequence !== incomingClip.sequence) {
    return ClipEventRealtime.Reorder;
  }
  if (clipInCache && clipInCache.name !== incomingClip.name) {
    return ClipEventRealtime.Renamed;
  }
}

export const useManageIncomingClipEvent = () => {
  const { search, selectedClipIds, setSelectedClipIds } =
    usePersistentClipStateAndActions();
  const {
    writeClipToCache,
    removeClipFromCache,
    softDeleteClipFromCache,
    updateClipInCache,
  } = useClipCacheUtilities();
  const [, { pauseAudioElementBySrc }] = useAudioContext();
  return ({
    incomingClip,
    clipInCache,
    event,
    projectId,
    changeType,
  }: ManageIncomingClipEventProps) => {
    switch (changeType) {
      case EntityChangeType.Created:
        // during search, avoid writing fragments
        if (search) {
          return;
        }
        // this case fixes the issues of invalidating the isCreditableBy
        if (!clipInCache?.isCreditableBy) {
          writeClipToCache(incomingClip, event.projectId);
        } else {
          writeClipToCache(
            { ...incomingClip, isCreditableBy: clipInCache.isCreditableBy },
            event.projectId
          );
        }
        break;

      case EntityChangeType.Deleted:
        pauseAudioElementBySrc(incomingClip.src);
        softDeleteClipFromCache(incomingClip);
        setSelectedClipIds(selectedClipIds.remove(incomingClip.id));
        break;

      case EntityChangeType.Updated: {
        const previousProjectId = event?.previousProjectId;
        const clipProjectId = incomingClip.projectId;
        // clip was sent away from project
        if (clipProjectId !== projectId && previousProjectId) {
          removeClipFromCache(incomingClip, previousProjectId);
          setSelectedClipIds(selectedClipIds.remove(incomingClip.id));
          break;
        }
        // during search, lock down any realtime update event that was not a clip moved from project
        if (search) {
          return;
        }
        // clip was received into project
        if (
          clipProjectId === projectId &&
          previousProjectId !== event.projectId
        ) {
          writeClipToCache(incomingClip, event.projectId);
          break;
        }
        // clip was updated: (ie reorder, rename, etc)
        if (incomingClip.isCreditableBy === '') {
          /**
           * Preserve cached value (subscription event does not have an updated value)
           * It's worth noting that there is an edge case that this does not address,
           * receiving incoming clips and not having the clip in the cache (IE during clip creation on another tab)
           */
          // eslint-disable-next-line no-param-reassign
          incomingClip.isCreditableBy = clipInCache?.isCreditableBy;
        }
        updateClipInCache(incomingClip, event.projectId);
        break;
      }
      default:
        // fallback: this should never fire - and if it does one of these conditional branches were not hit
        // eslint-disable-next-line no-console
        console.error(
          `error: something went wrong in useClipEntityChangedSubscription`
        );
    }
  };
};

export const useOnSubscriptionData = (projectId: string) => {
  const realtimeClipLogger = useRealtimeClipLogger(projectId);
  const isOutOfOrderEvent = useIsOutOfOrderEvent();
  const manageIncomingClipEvent = useManageIncomingClipEvent();
  const { clipInCacheById } = useClipCacheUtilities();

  return (data: OnDataOptions<ClipEntityChangedSubscription>) => {
    const clipEvent = data?.data?.data?.clipEntityChanged;
    if (!clipEvent) {
      // eslint-disable-next-line no-console
      console.error('clipEvent not found');
      return;
    }
    const { clipEntity, ...event } = clipEvent;
    if (!clipEntity) {
      // eslint-disable-next-line no-console
      console.error('clipEntity not found');
      return;
    }
    const outOfOrderEvent = isOutOfOrderEvent(
      clipEntity.id,
      clipEvent.eventTime
    );
    if (outOfOrderEvent) {
      // eslint-disable-next-line no-console
      console.info('out of order event occurred');
      return;
    }

    // casting the Subscription clip as a ClipObject
    const incomingClip: ClipObject = {
      ...clipEntity,
      __typename: 'Clip',
    };
    const clipId = incomingClip.id;
    const clipInCache = clipInCacheById(clipId);
    const changeType = event?.changeType;

    /**
     * Known issues with realtime events (note these are active client issues only)
     * These issues become redundant once we account for not triggering this code on
     * the active client
     * 1. move clip between project and undo results in clip restore event
     *
     */

    const clipRealtimeEvent = calculateRealtimeEvent({
      changeType,
      projectId,
      incomingClip,
      clipInCache,
    });

    if (clipRealtimeEvent) {
      const payload = {
        ...clipEvent,
        changeType: clipRealtimeEvent,
      };
      realtimeClipLogger(payload);
    }

    manageIncomingClipEvent({
      projectId,
      event,
      incomingClip,
      clipInCache,
      changeType,
    });

    return {
      clipRealtimeEvent,
    };
  };
};

/**
 * @description This hook subscribes a user to clip updates on a project w/ realtime capabilities
 * currently supports clip creation, deletion, update, and moving clips
 * @todo
 * its worth noting atm that we do trigger both realtime events and clip events for the client
 * who performed them
 */
const useClipEntityChanged = (projectId: string) => {
  const onData = useOnSubscriptionData(projectId);

  return useClipEntityChangedSubscription({
    variables: {
      inputs: {
        projectId,
      },
    },
    onData,
  });
};
export default useClipEntityChanged;
